import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Filter, Loader } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { unitService } from '../../../services/unitService';
import { REQUEST_STAGES, REQUEST_TYPES, studentRequestService } from '../../../services/studentRequestService';

const submittedFieldLabel = (key) => ({
    target_program: 'Target programme',
    target_program_code: 'Programme code',
    target_program_level: 'Programme level',
}[key] || String(key).replaceAll('_', ' '));

const ApprovalDashboard = ({ onBack }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('to_me'); // 'to_me' or 'made_by_me'
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [note, setNote] = useState('');
    const [rejectionRequest, setRejectionRequest] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [error, setError] = useState(null);
    const [studentRequestAttachments, setStudentRequestAttachments] = useState({ letter: null, academic: [] });
    const [previewAttachment, setPreviewAttachment] = useState(null);

    const sameId = (left, right) => left != null && right != null && String(left) === String(right);
    const normaliseStatus = (value) => {
        const status = String(value || 'pending').toLowerCase();
        return status === 'active' || status.endsWith('_approved') ? 'approved' : status;
    };
    const getStudentRequestStage = (roles) => {
        if (roles.some(role => ['system_administrator', 'chief_principal', 'vice_chancellor', 'vc', 'principal'].includes(role))) return 'all';
        if (roles.includes('head_of_department') || roles.includes('head_of_section')) return 'hod_review';
        if (roles.includes('registrar_academic') || roles.includes('deputy_principal_academic')) return 'academic_review';
        if (roles.includes('admissions_officer')) return 'admissions_review';
        if (roles.includes('finance_officer')) return 'finance_review';
        return null;
    };

    useEffect(() => {
        if (user?.id) loadRequests();
    }, [user?.id]);

    useEffect(() => {
        let cancelled = false;
        const payload = selectedRequest?.type === 'student_request'
            ? (selectedRequest.rawData?.request_payload || {})
            : null;
        if (!payload) {
            setStudentRequestAttachments({ letter: null, academic: [] });
            return undefined;
        }

        const loadAttachments = async () => {
            const letter = payload.letter_path
                ? await studentRequestService.getLetterUrl(payload.letter_path).catch(() => null)
                : null;
            const academic = Array.isArray(payload.academic_documents)
                ? await Promise.all(payload.academic_documents.map(async document => ({
                    name: document.name || 'Academic document',
                    url: document.path ? await studentRequestService.getLetterUrl(document.path).catch(() => null) : null,
                })))
                : [];
            if (!cancelled) setStudentRequestAttachments({ letter, academic: academic.filter(document => document.url) });
        };

        void loadAttachments();
        return () => { cancelled = true; };
    }, [selectedRequest?.id, selectedRequest?.type]);

    const loadRequests = async () => {
        try {
            setLoading(true);
            setError(null);

            // Load user profile and staff record to get department
            const [
                { data: profile, error: profileError },
                { data: staffRecord }
            ] = await Promise.all([
                supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single(),
                supabase?.from('staff')?.select('department_id')?.eq('profile_id', user?.id)?.maybeSingle()
            ]);

            if (profileError) console.warn('Approver profile could not be loaded:', profileError);
            const myDeptId = staffRecord?.department_id || profile?.department_id;
            const profileRoles = [
                profile?.primary_role,
                ...(Array.isArray(profile?.secondary_roles) ? profile.secondary_roles : [])
            ].filter(Boolean).map(role => String(role).toLowerCase());
            const hasGlobalApprovalScope = [
                'system_administrator',
                'chief_principal',
                'deputy_principal_academic',
                'registrar_academic'
            ].some(role => profileRoles.includes(role));

            // Load assessment approvals
            let evidenceQuery = supabase?.from('assessment_approvals')?.select(`
                *,
                evidence:assessment_evidence(
                  id,
                  student_assessment_id,
                  file_name,
                  file_path
                ),
                student_assessment:student_assessments(
                  student_id,
                  timeline_id
                ),
                approver:user_profiles!approver_id(
                  full_name,
                  email
                )
              `)?.order('created_at', { ascending: false });
            if (user?.id) {
                evidenceQuery = evidenceQuery?.eq('approver_id', user.id);
            }

            // Load assignments created by the signed-in user
            const madeAssignmentsPromise = supabase.rpc('get_unit_assignment_requests_made', { p_department_id: myDeptId || null })
                .then(res => {
                    if (res.error) throw res.error;
                    return { data: res.data || [] };
                })
                .catch(err => {
                    console.warn('Failed to fetch requests made RPC:', err);
                    return { data: [] };
                });

            const [{ data: evidenceApprovals, error: evidenceError }, assignmentResult, { data: madeAssignmentsData, error: madeError }] = await Promise.all([
                evidenceQuery,
                unitService.getPendingDepartmentApprovals(myDeptId)
                .then(data => ({ data, error: null }))
                .catch(error => ({ data: [], error })),
                madeAssignmentsPromise
            ]);
            
            if (evidenceError) console.warn('Assessment approvals are unavailable:', evidenceError);
            if (assignmentResult.error && evidenceError) throw assignmentResult.error;
            if (assignmentResult.error) console.warn('Trainer approvals are unavailable:', assignmentResult.error);
            if (madeError) console.warn('Failed to load requests made:', madeError);
            
            // get_unit_assignment_approvals also returns every pending row to a
            // global academic user.  Keep that capability, but never treat a
            // request made by the current user as an incoming request.
            const assignmentRows = (assignmentResult.data || []).filter(item => {
                const srcId = item.source_department_id || item.source_department?.id;
                const tgtId = item.target_department_id || item.target_department?.id;
                const isCrossDepartment = Boolean(srcId && tgtId && !sameId(srcId, tgtId));
                const isPending = String(item.status || '').toLowerCase() === 'pending';
                const isAssignedToMe = sameId(item.approver_id, user?.id);
                const isMyOwnRequest = sameId(item.assigned_by, user?.id)
                    || sameId(item.assigned_by_user?.id, user?.id);
                return isCrossDepartment && !isMyOwnRequest && (isAssignedToMe || (hasGlobalApprovalScope && isPending));
            });
            const madeAssignments = (madeAssignmentsData || []).filter(item => {
                const srcId = item.source_department_id || item.source_department?.id;
                const tgtId = item.target_department_id || item.target_department?.id;
                return Boolean(srcId && tgtId && !sameId(srcId, tgtId));
            });

            // Student academic requests are part of the HOD/HOS approval queue.
            // Leadership is assigned on departments/sections, so the service
            // resolves that scope instead of relying only on profile.department_id.
            const studentRequestStage = getStudentRequestStage(profileRoles);
            const studentRequestRows = studentRequestStage
                ? await studentRequestService.listQueue(
                    studentRequestStage,
                    myDeptId,
                    user?.id,
                    profile?.section_id
                ).catch(studentRequestError => {
                    console.warn('Student request approvals are unavailable:', studentRequestError);
                    return [];
                })
                : [];

            // Combine and format approvals
            const formattedRequests = [
                // assessment_approvals is an audit trail.  It does not contain
                // a requester field, so rows assigned to somebody else must
                // never be placed in “My Requests”.  Only the current
                // approver's rows belong in “Requests to Me”.
                ...(evidenceApprovals || [])?.map(item => ({
                    id: item?.id,
                    title: `Assessment Evidence - ${item?.evidence?.file_name || 'Document'}`,
                    type: 'evidence',
                    category: 'Assessment Evidence',
                    date: item?.created_at,
                    status: normaliseStatus(item?.approval_status),
                    direction: 'to_me',
                    requester: item?.student_assessment?.student_id || 'Unknown',
                    counterpartyLabel: 'Requester',
                    details: item?.comments || 'Please review the assessment evidence.',
                    rawData: item
                })),
                ...(assignmentRows || [])?.map(item => ({
                    id: item?.id,
                    title: `${item?.unit?.name || 'Unit'} - ${item?.unit?.code || ''}`,
                    type: 'assignment',
                    category: 'Trainer Assignment',
                    date: item?.assigned_at,
                    status: normaliseStatus(item?.status),
                    direction: 'to_me',
                    requester: item?.assigned_by_user?.full_name || 'Unknown',
                    counterpartyLabel: 'Requested by',
                    details: [
                        `Source department: ${item?.source_department?.name || item?.trainer?.department?.name || 'Not recorded'}`,
                        item?.expires_at ? `Decision due ${new Date(item.expires_at).toLocaleString()}` : null,
                        item?.decision_reason || null
                    ].filter(Boolean).join(' · '),
                    rawData: item
                })),
                ...(madeAssignments || [])?.map(item => ({
                    id: item?.id,
                    title: `${item?.unit?.name || 'Unit'} - ${item?.unit?.code || ''}`,
                    type: 'assignment',
                    category: 'Trainer Assignment',
                    date: item?.assigned_at,
                    status: normaliseStatus(item?.status),
                    direction: 'made_by_me',
                    requester: item?.target_department?.name || 'Target department not recorded',
                    counterpartyLabel: 'Destination',
                    details: [
                        `Requested by: ${item?.assigned_by_user?.full_name || 'Your department'}`,
                        `Target department: ${item?.target_department?.name || 'Not recorded'}`,
                        item?.expires_at ? `Decision due ${new Date(item.expires_at).toLocaleString()}` : null,
                        item?.decision_reason || null
                    ].filter(Boolean).join(' · '),
                    rawData: item
                })),
                ...(studentRequestRows || []).map(item => {
                    const requestType = REQUEST_TYPES[item.request_type];
                    const student = item.student || {};
                    const programme = student.program || {};
                    const registrationNumber = student.admission_number || student.registration_number || student.student_id;
                    const studentIdentifiers = [
                        { label: 'Name', value: student.full_name || student.email || 'Student' },
                        { label: 'Registration no.', value: registrationNumber },
                        { label: 'Programme', value: programme.name || student.program_name },
                        { label: 'Programme code', value: programme.code || student.program_code },
                        { label: 'Level', value: student.academic_level || student.program_level },
                        { label: 'Module', value: student.current_module || student.module_number || student.starting_module },
                    ].filter(identifier => identifier.value !== null && identifier.value !== undefined && String(identifier.value).trim() !== '');
                    const terminal = ['approved', 'rejected'].includes(String(item.status || '').toLowerCase());
                    const actionable = (studentRequestStage === 'all' || item.current_stage === studentRequestStage) && !terminal;
                    const auditField = {
                        hod_review: 'hod_reviewer_id',
                        academic_review: 'academic_reviewer_id',
                        admissions_review: 'admissions_reviewer_id',
                        finance_review: 'finance_reviewer_id',
                    }[studentRequestStage];
                    const actedByThisStage = studentRequestStage === 'all' || Boolean(auditField && item[auditField]);
                    const forNoting = terminal && !actedByThisStage;
                    return {
                        id: item.id,
                        title: requestType?.label || String(item.request_type || 'Student request').replaceAll('_', ' '),
                        type: 'student_request',
                        category: 'Student Request',
                        date: item.submitted_at || item.created_at,
                        status: forNoting ? 'for_noting' : terminal ? normaliseStatus(item.status) : actionable ? 'pending' : 'in_progress',
                        actionable,
                        direction: 'to_me',
                        requester: student.full_name || student.email || 'Student',
                        counterpartyLabel: 'Student',
                        details: [
                            item.request_number,
                            item.reason || 'Academic request awaiting HOD / HOS recommendation.',
                            ...studentIdentifiers.filter(identifier => identifier.label !== 'Name').map(identifier => `${identifier.label}: ${identifier.value}`),
                            (item.request_payload?.letter_path || item.request_payload?.academic_documents?.length)
                                ? `${Number(Boolean(item.request_payload?.letter_path)) + (Array.isArray(item.request_payload?.academic_documents) ? item.request_payload.academic_documents.length : 0)} attachment(s)`
                                : null,
                        ].filter(Boolean).join(' · '),
                        requestNumber: item.request_number,
                        requestReason: item.reason || 'Academic request awaiting HOD / HOS recommendation.',
                        studentIdentifiers,
                        stage: item.current_stage,
                        rawData: item,
                    };
                })
            ]?.sort((a, b) => new Date(b.date) - new Date(a.date));

            // Filter out exact duplicates that might appear in both lists (e.g. if global scope)
            const uniqueRequests = Array.from(new Map(formattedRequests.map(item => [item.id + '_' + item.direction, item])).values());

            setRequests(uniqueRequests);
        } catch (err) {
            console.error('Error loading approvals:', err);
            setError(err?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (approval, comment = '') => {
        try {
            setProcessing(approval?.id);
            setError(null);

            if (approval?.type === 'evidence') {
                const { error } = await supabase?.from('assessment_approvals')?.update({
                    approval_status: 'trainer_approved',
                    approved_at: new Date()?.toISOString()
                })?.eq('id', approval?.id);

                if (error) throw error;
            } else if (approval?.type === 'assignment') {
                await unitService.decideTrainerAssignment(approval.id, true);
            } else if (approval?.type === 'student_request') {
                const stage = approval.rawData?.current_stage;
                const decision = stage === 'hod_review' ? 'recommend' : stage === 'finance_review' ? 'actioned' : 'approve';
                await studentRequestService.decide(approval.id, decision, comment);
            }

            await loadRequests();
            setSelectedRequest(null);
            setNote('');
        } catch (err) {
            console.error('Error approving:', err);
            setError(err?.message);
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = (approval) => {
        setRejectionRequest(approval);
        setRejectionReason('');
    };

    const submitRejection = async () => {
        const reason = rejectionReason.trim();
        if (!rejectionRequest || !reason) return;
        try {
            setProcessing(rejectionRequest?.id);
            setError(null);

            if (rejectionRequest?.type === 'evidence') {
                const { error } = await supabase?.from('assessment_approvals')?.update({
                    approval_status: 'rejected',
                    comments: reason,
                    approved_at: new Date()?.toISOString()
                })?.eq('id', rejectionRequest?.id);

                if (error) throw error;
            } else if (rejectionRequest?.type === 'assignment') {
                await unitService.decideTrainerAssignment(rejectionRequest.id, false, reason);
            } else if (rejectionRequest?.type === 'student_request') {
                await studentRequestService.decide(rejectionRequest.id, 'reject', reason);
            }

            await loadRequests();
            setSelectedRequest(null);
            setRejectionRequest(null);
            setRejectionReason('');
        } catch (err) {
            console.error('Error rejecting:', err);
            setError(err?.message);
        } finally {
            setProcessing(null);
        }
    };

    const filteredRequests = requests.filter(req => {
        if (req.direction !== activeTab) return false;
        if (statusFilter !== 'all' && req.status !== statusFilter) return false;
        return true;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Approved
                </span>;
            case 'rejected':
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                    Rejected
                </span>;
            case 'in_progress':
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                    In progress
                </span>;
            case 'for_noting':
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    For noting
                </span>;
            case 'pending':
            default:
                return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    Pending
                </span>;
        }
    };

    const selectedStudentRequestActionable = selectedRequest?.type === 'student_request'
        && selectedRequest.direction === 'to_me'
        && selectedRequest.actionable !== false
        && !['approved', 'rejected', 'for_noting'].includes(selectedRequest.status);
    const selectedApprovalActionable = selectedRequest
        && selectedRequest.direction === 'to_me'
        && (selectedStudentRequestActionable || (selectedRequest.type !== 'student_request' && selectedRequest.status === 'pending'));

    return (
        <div className="space-y-6">
            {/* Tabs and Filters */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 shadow-sm border border-slate-100 sm:gap-4 sm:p-4">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="shrink-0 text-sm font-semibold text-blue-700 hover:underline"
                        >
                            ← Staff dashboard
                        </button>
                    )}
                    <div className="flex max-w-full shrink-0 whitespace-nowrap overflow-x-auto bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setActiveTab('to_me')}
                        className={cn(
                            "shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-all",
                            activeTab === 'to_me'
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        )}
                    >
                        Requests to Me
                    </button>
                    <button
                        onClick={() => setActiveTab('made_by_me')}
                        className={cn(
                            "shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-all",
                            activeTab === 'made_by_me'
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        )}
                    >
                        My Requests
                    </button>
                    </div>
                </div>

                <div className="flex w-full items-center gap-2 sm:w-auto">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="min-w-0 flex-1 text-sm border-slate-200 rounded-lg bg-slate-50 py-2 pl-3 pr-8 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow sm:flex-none"
                    >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="for_noting">For noting</option>
                    </select>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Request List */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-12 flex justify-center items-center">
                            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">S/N</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        {activeTab === 'to_me' ? 'Requester' : 'Destination'}
                                    </th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredRequests.length > 0 ? (
                                    filteredRequests.map((req, index) => (
                                        <tr key={req.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="py-4 px-4 text-sm text-slate-400">
                                                {(index + 1).toString().padStart(2, '0')}
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="inline-flex items-center gap-1.5 font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded-md text-xs">
                                                    <FileText className="w-3 h-3 text-slate-400" />
                                                    {req.category}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                                                        {req.title}
                                                    </span>
                                                    <span className="text-xs text-slate-500 mt-0.5 line-clamp-1">{req.details}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="text-sm text-slate-600">{req.requester}</span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="text-sm text-slate-500">{new Date(req.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-2">
                                                    {getStatusBadge(req.status)}
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <button 
                                                    onClick={() => { setSelectedRequest(req); setNote(''); }}
                                                    className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center text-slate-400">
                                                <FileText className="w-12 h-12 mb-3 text-slate-200" />
                                                <p className="text-sm font-medium text-slate-600">No requests found</p>
                                                <p className="text-xs mt-1 text-slate-400">Try adjusting your filters or check back later.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal */}
            {selectedRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6">
                    <div className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100vh-3rem)]">
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-4 sm:px-6">
                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Approval record</p>
                                <h3 className="mt-1 truncate font-semibold text-slate-900">Request Details</h3>
                            </div>
                            <button 
                                onClick={() => setSelectedRequest(null)}
                                className="text-slate-400 hover:text-slate-600 transition-colors"
                                aria-label="Close request details"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                            <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
                            <div className="space-y-2 lg:col-span-2">
                                <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Request details</label>
                                <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:grid-cols-[minmax(170px,0.35fr)_minmax(0,1fr)] sm:p-4">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Request number</p>
                                        <p className="mt-1 break-all font-mono text-sm font-semibold text-slate-800">{selectedRequest.requestNumber || selectedRequest.rawData?.request_number || '—'}</p>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Reason / supporting context</p>
                                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-700">{selectedRequest.requestReason || selectedRequest.rawData?.reason || selectedRequest.details || '—'}</p>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Request type</p>
                                        <p className="mt-0.5 break-words text-sm font-semibold text-slate-800">{selectedRequest.title}</p>
                                    </div>
                                     {selectedRequest.type === 'student_request' && Object.entries(selectedRequest.rawData?.request_payload || {}).some(([key, value]) => !['letter_path', 'letter_name', 'letter_mime', 'academic_documents', 'target_program_id'].includes(key) && value !== '' && value !== null && value !== undefined && !Array.isArray(value)) && (
                                        <div className="sm:col-span-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Submitted request fields</p>
                                            <div className="mt-1 grid gap-2 sm:grid-cols-3">
                                                 {Object.entries(selectedRequest.rawData?.request_payload || {}).filter(([key, value]) => !['letter_path', 'letter_name', 'letter_mime', 'academic_documents', 'target_program_id'].includes(key) && value !== '' && value !== null && value !== undefined && !Array.isArray(value)).map(([key, value]) => (
                                                     <div key={key} className="min-w-0 rounded-md border border-slate-200 bg-white px-2.5 py-2">
                                                         <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{submittedFieldLabel(key)}</p>
                                                        <p className="mt-0.5 break-words text-xs font-medium text-slate-800">{String(value)}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label className="text-xs font-medium text-slate-500 uppercase">Category</label>
                                    <p className="text-sm text-slate-700">{selectedRequest.category}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500 uppercase">Date</label>
                                    <p className="text-sm text-slate-700">{new Date(selectedRequest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                </div>
                            <div>
                                <label className="text-xs font-medium text-slate-500 uppercase">Status</label>
                                <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                            </div>
                            {selectedRequest.type === 'student_request' && (
                                <div>
                                    <label className="text-xs font-medium text-slate-500 uppercase">Workflow stage</label>
                                    <p className="text-sm text-slate-700">{REQUEST_STAGES[selectedRequest.stage || selectedRequest.rawData?.current_stage]?.label || 'Institutional review'}</p>
                                </div>
                            )}
                            </div>
                            <div className="min-w-0">
                                <label className="text-xs font-medium text-slate-500 uppercase">{selectedRequest.counterpartyLabel || 'Requester'}</label>
                                <p className="break-words text-sm text-slate-700">{selectedRequest.requester}</p>
                            </div>
                            </div>
                            <div className="space-y-3">
                            {selectedRequest.type === 'student_request' && (
                                <div>
                                    <label className="text-xs font-medium text-slate-500 uppercase">Attachments</label>
                                    <div className="mt-1 grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:grid-cols-2">
                                        {studentRequestAttachments.letter && (
                                            <button type="button" onClick={() => setPreviewAttachment({ url: studentRequestAttachments.letter, name: selectedRequest.rawData?.request_payload?.letter_name || 'Written request letter' })} className="flex min-w-0 items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-50 hover:underline">
                                                <FileText className="mt-0.5 h-4 w-4 shrink-0" />
                                                <span className="min-w-0 break-all">{selectedRequest.rawData?.request_payload?.letter_name || 'Open written request letter'}</span>
                                            </button>
                                        )}
                                        {studentRequestAttachments.academic.map(document => (
                                            <button type="button" key={document.url} onClick={() => setPreviewAttachment({ url: document.url, name: document.name })} className="flex min-w-0 items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-50 hover:underline">
                                                <FileText className="mt-0.5 h-4 w-4 shrink-0" />
                                                <span className="min-w-0 break-all">{document.name}</span>
                                            </button>
                                        ))}
                                        {!studentRequestAttachments.letter && studentRequestAttachments.academic.length === 0 && (
                                            <p className="text-sm text-slate-500 sm:col-span-2">No attachments available.</p>
                            )}
                            </div>
                            </div>
                            )}
                            </div>
                            {selectedRequest.type === 'student_request' && selectedRequest.studentIdentifiers?.length > 0 && (
                                <div className="space-y-2 lg:col-span-2">
                                    <label className="text-xs font-medium uppercase text-slate-500">Student identifiers</label>
                                    <div className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-3">
                                        {selectedRequest.studentIdentifiers.map(identifier => (
                                            <div key={identifier.label} className="min-w-0 rounded-md border border-slate-200 bg-white px-3 py-2">
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{identifier.label}</p>
                                                <p className="mt-0.5 break-words text-sm font-medium text-slate-800">{String(identifier.value)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedRequest.type !== 'student_request' && (
                                <div className="lg:col-span-2">
                                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Details</label>
                                    <p className="mt-1 min-h-12 break-words rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">{selectedRequest.details || '—'}</p>
                                </div>
                            )}
                        </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4 sm:px-6">
                            {selectedStudentRequestActionable && (
                                <div className="w-full">
                                    <label htmlFor="student-request-comment-footer" className="text-xs font-medium uppercase text-slate-500">Recommendation comment</label>
                                    <textarea
                                        id="student-request-comment-footer"
                                        value={note}
                                        onChange={event => setNote(event.target.value)}
                                        rows={2}
                                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                        placeholder="Add a commendation, recommendation, or note for the next office (optional)."
                                    />
                                </div>
                            )}
                            <button 
                                onClick={() => setSelectedRequest(null)}
                                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                Close
                            </button>
                            {selectedApprovalActionable && (
                                <>
                                    <button 
                                        onClick={() => handleReject(selectedRequest)}
                                        disabled={processing === selectedRequest.id}
                                        className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 disabled:opacity-50 flex items-center gap-2">
                                        {processing === selectedRequest.id && <Loader className="w-3 h-3 animate-spin" />}
                                        Reject
                                    </button>
                                    <button 
                                        onClick={() => handleApprove(selectedRequest, note.trim())}
                                        disabled={processing === selectedRequest.id}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2">
                                        {processing === selectedRequest.id && <Loader className="w-3 h-3 animate-spin" />}
                                        {selectedRequest.type === 'student_request'
                                            ? selectedRequest.rawData?.current_stage === 'hod_review'
                                                ? 'Recommend'
                                                : selectedRequest.rawData?.current_stage === 'finance_review'
                                                    ? 'Mark actioned'
                                                    : 'Approve'
                                            : 'Approve'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {rejectionRequest && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="rejection-dialog-title">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
                            <div>
                                <h3 id="rejection-dialog-title" className="text-base font-semibold text-slate-900">Reject request</h3>
                                <p className="mt-1 text-sm text-slate-500">Record a reason for the student and the next reviewing office.</p>
                            </div>
                            <button type="button" onClick={() => { setRejectionRequest(null); setRejectionReason(''); }} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Cancel rejection">
                                <XCircle className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="space-y-3 p-5">
                            <label htmlFor="rejection-reason" className="text-sm font-medium text-slate-700">Rejection reason <span className="text-red-600">*</span></label>
                            <textarea
                                id="rejection-reason"
                                autoFocus
                                value={rejectionReason}
                                onChange={event => setRejectionReason(event.target.value)}
                                rows={4}
                                className="w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                                placeholder="Explain why this request is being rejected."
                            />
                            <div className="flex justify-end gap-2 pt-1">
                                <button type="button" onClick={() => { setRejectionRequest(null); setRejectionReason(''); }} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
                                <button type="button" onClick={submitRejection} disabled={!rejectionReason.trim() || processing === rejectionRequest.id} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                                    {processing === rejectionRequest.id && <Loader className="h-4 w-4 animate-spin" />}
                                    Reject request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {previewAttachment && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-6">
                    <div className="flex h-[calc(100vh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:h-[calc(100vh-3rem)]">
                        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5">
                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Attachment preview</p>
                                <h3 className="truncate text-sm font-semibold text-slate-900 sm:text-base">{previewAttachment.name}</h3>
                            </div>
                            <button type="button" onClick={() => setPreviewAttachment(null)} className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700" aria-label="Close attachment preview">
                                <XCircle className="h-6 w-6" />
                            </button>
                        </div>
                        <iframe src={previewAttachment.url} title={previewAttachment.name} className="min-h-0 w-full flex-1 bg-slate-100" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalDashboard;
